import React, { useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';

import cookies from 'js-cookie';
import { Provider, useDispatch, useSelector } from 'react-redux';
import store from './redux/createStore';
import './i18n';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/charts/styles.css';
import 'mantine-react-table/styles.css'; //import MRT styles
import '@mantine/carousel/styles.css';
import * as serviceWorker from './serviceWorker';
import AppRouter from './AppRouter';
import { getData } from './utils/api-service';
import { clearUserAction, setUserAction } from './redux/actions';
import { Loader, MantineProvider } from '@mantine/core';
import { theme } from './theme';
import { Notifications } from '@mantine/notifications';
import LoaderOverlay from './components/LoaderOverlay/LoaderOverlay';
import {
  useQuery,
  useMutation,
  useQueryClient,
  QueryClient,
  QueryClientProvider,
  QueryCache
} from '@tanstack/react-query';
// import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import i18n from './i18n';
import { showNotification } from './utils/Notification/Notification';
import { ModalsProvider } from '@mantine/modals';
import { PostHogProvider, usePostHog } from 'posthog-js/react';
import { useTranslation } from 'react-i18next';
import { identifyUser } from './utils/monitoring/posthog';
import 'react-phone-number-input/style.css';
import { history } from './history';
import { identifySentryUser } from './utils/monitoring/sentry';
import moment from 'moment';
import { DatesProvider } from '@mantine/dates';
import 'moment/min/locales';
import 'dayjs/locale/es'; // Example for Spanish locale
import 'dayjs/locale/en'; // Example for English locale
import { syncClientVersionCookie } from './utils/clientVersion';
import { onSessionInvalidated } from './services/sessionSecurity';
import { clearSessionData } from './services/sessionCleanup';
import WashdayClientSingleton from './services/WashdayClientSingleton';
import { disconnectActiveSocket } from './hooks/useSocket/useSocket';
import { useSessionVerification } from './hooks/auth/useSessionVerification';
import { shutdown } from '@intercom/messenger-js-sdk';

const isPublicCustomerDisplay = window.location.pathname === '/customer-display' || window.location.pathname.startsWith('/customer-display/');

if (!isPublicCustomerDisplay) Sentry.init({
  dsn: import.meta.env.REACT_APP_SENTRY_DNS,
  environment: import.meta.env.NODE_ENV,
  sendDefaultPii: true,
  tracesSampleRate: 0.5,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  integrations: [
    Sentry.reactRouterV5BrowserTracingIntegration({
      history
    }),
    Sentry.replayIntegration(),
    Sentry.consoleLoggingIntegration({ levels: ['error', 'warn'] })
  ],
  _experiments: {
    enableLogger: true
  }
});

// helper to map i18n language to a moment locale key
export const resolveMomentLocale = (lng = 'es') => (lng.startsWith('es') ? 'es' : 'en');
moment.locale(resolveMomentLocale(i18n.language));

const stripePromise = isPublicCustomerDisplay ? Promise.resolve(null) : loadStripe(import.meta.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false
    }
  },
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (query.meta?.silent) return;
      showNotification({ type: 'Error', title: i18n.t('Notifications:somethingWrong') });
    }
  })
});

onSessionInvalidated((_token, reason) => {
  clearSessionData({
    queryClient, dispatch: store.dispatch,
    clearSdk: () => WashdayClientSingleton.clear(),
    disconnect: disconnectActiveSocket,
    redirect: (path) => window.location.replace(path)
  }, reason);
  Sentry.setUser(null);
  shutdown();
});

const currentVersion = 4.2;
syncClientVersionCookie(cookies, currentVersion);

const AppInitializer = () => {
  useTranslation(['Notifications', 'Backend', 'CommonButtons']);
  const dispatch = useDispatch();
  useSessionVerification();
  const datesSettings = useMemo(() => {
    return ({ locale: resolveMomentLocale(i18n.language) });
  }, [i18n.language]);

  const posthog = usePostHog();
  const user = useSelector((state) => state.user);
  const token = cookies.get('token');

  useEffect(() => {
    async function checkAuthentication() {
      const token = cookies.get('token');

      if (token && !user.user?._id) {
        try {
          // Intenta autenticar al usuario
          const res = await getData('api/auth');
          if (cookies.get('token') !== token) return;
          // Verifica si la autenticación fue exitosa
          if (res?.data?.data?.token && res?.data?.data?.user) {
            // Identify sends an event, so you want may want to limit how often you call it
            identifyUser(posthog, res?.data?.data?.user, res?.data?.data?.company?._id);
            identifySentryUser({
              _id: res?.data?.data?.user?._id,
              email: res?.data?.data?.user?.email,
              name: res?.data?.data?.user?.name
            });
            dispatch(
              setUserAction({
                token: res?.data?.data?.token || '',
                company: res?.data?.data?.company || {},
                hasMultiStore: res?.data?.data?.user?.hasMultiStore || false,
                user: res?.data?.data?.user || {},
                role: res?.data?.data?.user?.role || '',
                isCompanyAdmin: res?.data?.data?.user?.isCompanyAdmin || false,
                cashierBox: res?.data?.data?.user?.cashierBox || '',
                store: res?.data?.data?.user?.store || '',
                name: res?.data?.data?.user?.name || '',
                stripeCustomerId: res?.data?.data?.user?.stripeCustomerId || '',
                permission: res?.data?.data?.user?.permission || {},
                intercom: res?.data?.data?.intercom ?? null
              })
            );
            // window.location = '/';
          } else {
            dispatch(clearUserAction());
          }
        } catch (error) {
          // Manejo de errores: muestra un mensaje de error o redirige al usuario a la página de inicio de sesión
          //REDUX CLEAR STATE AND COOKIES
          if (cookies.get('token') === token) dispatch(clearUserAction());
        }
      }
    }

    checkAuthentication();
  }, []);

  if (token && !user.user?._id) {
    //En caso de tener token en cookies y no usuario en el state de redux, mostrara loading mientras hace request
    return <LoaderOverlay />;
  }
  return (
    <DatesProvider settings={datesSettings}>
      <AppRouter />
    </DatesProvider>
  );
};

const resolver = (theme) => ({
  variables: {
    '--mantine-main-content-height': theme.other.mainContentHeight,
    '--mantine-washday-pages-bg-color': theme.other.pagesBackGroundColor,
    '--mantine-washday-secondary-color': theme.other.washdaySecondaryColor
  }
});

const options = {
  api_host: import.meta.env.REACT_APP_PUBLIC_POSTHOG_HOST,

  // 🔇 Apaga todo lo automático
  autocapture: false, // no clicks/inputs/forms automáticos
  capture_pageview: false, // no $pageview automático
  capture_pageleave: false, // no $pageleave automático
  capture_dead_clicks: false, // no "dead clicks" automáticos
  rageclick: false // no rageclicks
};
const root = createRoot(document.getElementById('root'));
root.render(
  isPublicCustomerDisplay ? (
    <Provider store={store}>
      <MantineProvider theme={theme} cssVariablesResolver={resolver}>
        <React.Suspense fallback={<LoaderOverlay />}>
          <AppRouter />
        </React.Suspense>
      </MantineProvider>
    </Provider>
  ) : (
  <React.StrictMode>
    <Provider store={store}>
      <PostHogProvider apiKey={import.meta.env.REACT_APP_PUBLIC_POSTHOG_KEY} options={options}>
        <QueryClientProvider client={queryClient}>
          <MantineProvider theme={theme} cssVariablesResolver={resolver}>
            <React.Suspense fallback={<LoaderOverlay />}>
              <ModalsProvider>
                <Elements stripe={stripePromise}>
                  <Notifications position="top-right" />
                  <AppInitializer />
                </Elements>
              </ModalsProvider>
            </React.Suspense>
          </MantineProvider>
          {/* <ReactQueryDevtools initialIsOpen={false} buttonPosition='bottom-left'/> */}
        </QueryClientProvider>
      </PostHogProvider>
    </Provider>
  </React.StrictMode>
  )
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
